import { z } from "zod";
import { defineStepConfig } from "../../../registry/helpers";

/**
 * Compute Order Facts Step (workflow-native approval flow).
 *
 * Computes and STAMPS approval facts onto the relational draft lines — list
 * price, list-price mismatch (price below list with no approved discount),
 * no-list-price, and product group — so downstream Approval steps can evaluate
 * their `appliesWhen` conditions against plain stamped fields without re-running
 * the discount/list-price service. Pass-through: emits the same draft ids.
 *
 * This is also the natural re-entry "breakpoint": after an action (e.g. adding a
 * missing list price) the workflow re-runs from here to re-evaluate.
 *
 * Backend-only (DB + discount/list-price access); handler in
 * backend/src/workflows/steps/order-management/.
 */
export const computeOrderFactsStep = defineStepConfig({
  type: "compute_order_facts",
  name: "Compute Order Facts",
  description:
    "Computes and stamps approval facts (list price, mismatch, product group) onto draft sales order lines for downstream Approval conditions",
  category: "Data Access",
  libraryModule: "OrderManagement",
  inputSchema: z.object({
    draftSalesOrderIds: z
      .array(z.string().uuid())
      .describe("UUIDs of the draft sales orders to compute facts for"),
  }),

  outputSchema: z.object({
    draftSalesOrderIds: z
      .array(z.string().uuid())
      .describe(
        "Pass-through draft sales order ids (facts are stamped on rows)",
      ),
    stampedOrderCount: z
      .number()
      .int()
      .describe("Number of orders whose facts changed"),
    ordersNeedingListPrice: z
      .array(z.string().uuid())
      .describe(
        "Draft sales order ids with >=1 line missing a list price. Wire to a workflow_action (e.g. 'add list price'); on completion, re-run Compute Order Facts to re-evaluate.",
      ),
  }),

  configSchema: z.object({}),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "5 minutes",
    retry: {
      maximumAttempts: 2,
    },
    nonRetryableErrorTypes: ["ValidationError", "SchemaValidationError"],
  },
});
